import TrackingEvent, { TrackingProperty } from 'constants/trackingEvents';
import { useCurrentUser } from 'hooks/new/useCurrentUser';
import { Space } from 'interfaces/space';
import { useRouter } from 'next/router';
import * as globalSpaceCss from 'styles/spaces.css';
import { DEFAULT_SPACE_ICON, SPACE_ICONS } from 'utils/constants';
import { pathnames } from 'utils/routerHelpers';
import { isAdmin } from 'utils/userHelpers';

import React from 'react';

import { LockSimple } from '@thinkific/chalk/react-icons';
import { MenuItemWithSymbols } from '@thinkific/chalk/recipes/MenuItemWithSymbols';
import { classNames } from '@thinkific/chalk/utils';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useCommunityContext } from 'components/CommunityContext';
import MenuIcon from 'components/new/ui/MenuIcon/MenuIcon';

import * as css from './SpacesItem.css';

interface SpacesItemProps {
  goToFunction: (filter: string, name: string) => void;
  space: Space;
}

export default function SpacesItem({ space, goToFunction }: SpacesItemProps) {
  const { currentUser, trackEvent, community } = useCommunityContext();
  const { getSpaceInteraction } = useCurrentUser(community.id);
  const canSort = !isAdmin(currentUser);
  const router = useRouter();

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: space.id,
    disabled: canSort,
  });

  const IconToDisplay = SPACE_ICONS[space.iconName ? space.iconName : DEFAULT_SPACE_ICON];

  const interaction = getSpaceInteraction(space.id);

  return (
    <li
      {...attributes}
      {...listeners}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <MenuItemWithSymbols
        active={
          router.pathname.includes(pathnames.spaces.space) && router.query.spaceId === space.id
        }
        as="span"
        className={classNames(css.spaceItem)}
        data-qa="space-item"
        endSymbol={
          interaction.hasUnreadPosts && <div className={css.unreadDot} data-space-id={space.id} />
        }
        label={space.name}
        leadSymbol={
          <MenuIcon
            iconName={IconToDisplay}
            iconSize="sm"
            statusIndicator={
              space.isExclusive && <LockSimple className={globalSpaceCss.privateSpace} filled />
            }
            hasSurface
          />
        }
        ref={setNodeRef}
        onClick={() => {
          goToFunction(space.id, space.name);
          if (interaction.hasUnreadPosts) {
            trackEvent(TrackingEvent.UNREAD_POSTS_READ, {
              [TrackingProperty.SPACE_ID]: space.id,
            });
          }
        }}
      >
        {space.name}
      </MenuItemWithSymbols>
    </li>
  );
}
